import * as React from 'react';
import { clsx } from 'clsx';
import { IProps } from '@/layouts/BlockWrapper/BlockWrapper.interface';
import '@/layouts/BlockWrapper/BlockWrapper.styles.css'; // Import styles

const BlockWrapper: React.FC<IProps> = (props) => {
    const {
        id,
        className,
        paddingTop,
        paddingBottom,
        dataType,
        layout = 'fullwidth', // Default to fullwidth
        backgroundColor,
        backgroundImage,
        backgroundGradient,
        invertTextColor = false,
        children,
        ...rest
    } = props;

    // --- Style Objects ---
    const outerStyle: React.CSSProperties = {};
    const heroStyle: React.CSSProperties = {};

    // --- Class Names ---
    let outerClasses = clsx(
        'blockWrapper',
        className,
        paddingTop && `blockWrapper__paddingTop--${paddingTop}`,
        paddingBottom && `blockWrapper__paddingBottom--${paddingBottom}`,
        { 'force-text-white': invertTextColor },
    );
    let heroContainerClasses = clsx('blockWrapper__hero-container');

    // --- Apply Backgrounds ---
    if (layout === 'fullwidth') {
        // Fullwidth background is applied to the outer wrapper
        if (backgroundColor)
            outerClasses = clsx(outerClasses, `background-${backgroundColor}`); // Default outer background for fullwidth layout
        if (backgroundGradient)
            outerClasses = clsx(outerClasses, `background-${backgroundGradient}`);
        if (backgroundImage)
            outerStyle.backgroundImage = `url(${backgroundImage.defaultSrc})`;
        // Potentially add background size, repeat etc. if needed
        if (backgroundImage) {
            outerStyle.backgroundSize = 'cover';
            outerStyle.backgroundPosition = 'center';
            outerStyle.backgroundRepeat = 'no-repeat';
            outerClasses = clsx(outerClasses, `blockWrapper--with-image`); // Ensure this class is added
        }
    } else if (layout === 'hero') {
        // Hero background is applied to the hero container
        // Outer container defaults to white unless specified otherwise (could add another prop if needed)
        outerStyle.backgroundColor = 'white'; // Default outer background for hero layout

        if (backgroundColor)
            heroContainerClasses = clsx(
                heroContainerClasses,
                `background-${backgroundColor}`,
            ); // Default hero background for hero layout
        if (backgroundImage) {
            heroStyle.backgroundImage = `url(${backgroundImage.defaultSrc})`;
            heroStyle.backgroundSize = 'cover';
            heroStyle.backgroundPosition = 'center';
            heroStyle.backgroundRepeat = 'no-repeat';
            heroContainerClasses = clsx(heroContainerClasses, 'blockWrapper__hero-container--with-image'); // Add this line
        }
        if (backgroundGradient)
            heroContainerClasses = clsx(
                heroContainerClasses,
                `background-${backgroundGradient}`,
            ); // Gradient overrides color/image
    }

    return (
        <section
            {...rest}
            id={id}
            data-type={dataType || `block - ${layout} `}
            className={outerClasses}
            style={outerStyle}
        >
            {layout === 'hero' ? (
                <div className={heroContainerClasses} style={heroStyle}>
                    <div className="blockWrapper__hero-content">
                        {children}
                    </div>
                </div>
            ) : (
                // Fullwidth layout places content container directly inside outer wrapper
                <div className="blockWrapper__fullwidth-content">{children}</div>
            )}
        </section>
    );
}

BlockWrapper.displayName = 'BlockWrapper';

export default BlockWrapper;
